import React, { useEffect, useRef } from 'react';
import { Card } from './ui/card';
import {
  Clock,
  Users,
  Award,
  Shield,
  Heart,
  Zap,
  Star,
  CheckCircle,
} from 'lucide-react';

export const Features = () => {
  const featuresRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scale-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = featuresRef.current?.querySelectorAll('.feature-item');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Clock,
      title: '24/7 Emergency Care',
      description:
        'Round-the-clock emergency services with immediate medical attention.',
      stats: 'Always Available',
    },
    {
      icon: Users,
      title: 'Expert Medical Team',
      description:
        'Highly qualified and experienced doctors across all specialties.',
      stats: '200+ Doctors',
    },
    {
      icon: Award,
      title: 'Award-Winning Care',
      description:
        'Recognized nationally for excellence in patient care and safety.',
      stats: '15+ Awards',
    },
    {
      icon: Shield,
      title: 'Patient Safety First',
      description:
        'Strict protocols and advanced systems ensuring maximum safety.',
      stats: '99.9% Safety Rate',
    },
    {
      icon: Zap,
      title: 'Advanced Technology',
      description:
        'State-of-the-art medical equipment and diagnostic facilities.',
      stats: 'Latest Tech',
    },
    {
      icon: Heart,
      title: 'Compassionate Care',
      description:
        'Patient-centered approach with empathy and personalized attention.',
      stats: '50K+ Happy Patients',
    },
  ];

  return (
    <section
      id="features"
      className="py-24 relative overflow-hidden"
      ref={featuresRef}
      style={{
        background: 'linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--muted)) 50%, hsl(var(--background)) 100%)'
      }}
    >
      {/* Decorative Background */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[hsl(var(--primary))]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[hsl(var(--secondary))]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full">
            <Star className="w-5 h-5 text-[hsl(var(--primary))]" />
            <span className="text-sm font-semibold text-[hsl(var(--primary))]">
              Why Choose Us
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))]">
            Excellence in Healthcare
            <span className="block mt-2 text-gradient">That You Can Trust</span>
          </h2>
          <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
            We combine medical expertise with compassionate care to provide the best
            health outcomes for our patients.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="feature-item opacity-0"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <Card className="h-full p-8 hover:shadow-[var(--shadow-xl)] transition-all duration-300 group hover:-translate-y-2 bg-[hsl(var(--card))] border-2 border-transparent hover:border-[hsl(var(--primary))]/20">
                <div className="flex flex-col h-full space-y-4">
                  {/* Icon */}
                  <div className="flex items-start justify-between">
                    <div
                      className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] flex items-center justify-center shadow-[var(--shadow-md)] group-hover:shadow-[var(--shadow-lg)] transition-all duration-300 group-hover:scale-110"
                    >
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex items-center space-x-1 text-[hsl(var(--success))]">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 space-y-3">
                    <h3 className="text-xl font-bold text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))] transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">
                      {feature.description}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="pt-4 border-t border-[hsl(var(--border))]">
                    <span className="text-sm font-semibold text-[hsl(var(--primary))]">
                      {feature.stats}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>

        {/* Image Section */}
        <div className="mt-20 grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-[hsl(var(--foreground))]">
              Modern Facilities,
              <span className="block text-gradient">World-Class Care</span>
            </h3>
            <p className="text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
              Our hospital is equipped with the latest medical technology and modern
              facilities to ensure you receive the highest standard of care in a
              comfortable environment.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[hsl(var(--card))] p-4 rounded-xl shadow-[var(--shadow-sm)]">
                <p className="text-3xl font-bold text-[hsl(var(--primary))]">20+</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Hospital Beds</p>
              </div>
              <div className="bg-[hsl(var(--card))] p-4 rounded-xl shadow-[var(--shadow-sm)]">
                <p className="text-3xl font-bold text-[hsl(var(--primary))]">Dedicated</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">ICU Units</p>
              </div>
            </div>
          </div>
          <div className="relative group">
            <div className="relative rounded-3xl overflow-hidden shadow-[var(--shadow-xl)] transition-all duration-500 group-hover:scale-105">
              <img
                src="https://images.pexels.com/photos/3844581/pexels-photo-3844581.jpeg"
                alt="Modern Medical Technology"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--primary))]/30 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};